import React, { useState, useContext, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Flex,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  RadioGroup,
  Stack,
  Radio,
  Spinner,
  Center,
  Heading,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faSave } from "@fortawesome/free-solid-svg-icons";
import { AlertsContext } from "providers/alerts";
import { apiRequest } from "utils/api";
import BoxHeader from "components/elements/BoxHeader";
import LocationAutocomplete from "components/elements/LocationAutocomplete";
import MoneyField from "components/elements/MoneyInput";
import PercentField from "components/elements/PercentField";
import { PropertyType, PropertyField } from "types/property";
import moment from "moment";
import { getErrorMessage } from "utils/errorHandler";
import { useCustomizations } from "hooks/customizations/useCustomizations";

interface EditPropertyProps {
  property: PropertyType | null;
  propertyDetailFields: PropertyField[];
  setPropertyDetailFields: React.Dispatch<React.SetStateAction<PropertyField[]>>;
  onSave: () => void;
  onCancel: () => void;
  isEditMode: boolean;
}

export function PropertyFieldsEdit({
  property,
  propertyDetailFields,
  setPropertyDetailFields,
  onSave,
  onCancel,
  isEditMode,
}: EditPropertyProps) {
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
  const [subType, setSubType] = useState<string>("");
  const [numberOfUnits, setNumberOfUnits] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const { showSuccess, showError } = useContext(AlertsContext);
  const [missingFieldIds] = useState<string[]>([]);
  const [, setLocationData] = useState<any>(null);
  const [, updateState] = useState<object>();
  const forceUpdate = useCallback(() => updateState({}), []);
  const { isAmplend } = useCustomizations();

  useEffect(() => {
    if (property && propertyDetailFields) {
      const values = propertyDetailFields.reduce((acc: Record<string, any>, field) => {
        if (field.data_key === "individual_unit_information") {
          if (field.values && Array.isArray(field.values)) {
            field.values.forEach((unitValue, index) => {
              if (unitValue.value && typeof unitValue.value === "object") {
                Object.entries(unitValue.value).forEach(([key, value]) => {
                  acc[`${key}_${index + 1}`] = (value as any).field || value;
                });
              }
            });
          }
        } else {
          const rawValue = field.values?.[0]?.value as any;
          if (field.type === "date" && rawValue && rawValue?.value !== "") {
            const dateValue =
              typeof rawValue === "object" && "field" in rawValue ? rawValue.field : rawValue;
            const parsedDate = moment(dateValue);
            if (parsedDate.isValid()) {
              const formattedDate = parsedDate.utc().toDate();
              acc[field.data_key] = formattedDate;
            } else {
              console.error("Invalid date value:", dateValue);
              acc[field.data_key] = null;
            }
          } else if (field.type === "date" && !rawValue) {
            acc[field.data_key] = null;
          } else if (typeof rawValue === "object" && rawValue !== null && "field" in rawValue) {
            acc[field.data_key] = rawValue.field ?? "";
          } else {
            acc[field.data_key] = (rawValue?.value ? rawValue.value : "") ?? "";
          }
        }
        return acc;
      }, {});

      setFieldValues(values);

      const initialSubType = values["sub_type"] ?? "";
      const initialNumberOfUnits = parseInt(values["number_of_units"] ?? "0");

      setSubType(initialSubType);
      setNumberOfUnits(initialNumberOfUnits);

      forceUpdate();
    }
  }, [property, propertyDetailFields, forceUpdate]);

  const handleFieldUpdate = (fieldId: string, value: any) => {
    setFieldValues(prev => {
      const newValues = { ...prev, [fieldId]: value };
      return newValues;
    });

    if (fieldId === "number_of_units") {
      const numUnits = parseInt(value) || 0;
      setNumberOfUnits(numUnits);
    } else if (fieldId === "appraisal_date") {
      setFieldValues(prev => ({
        ...prev,
        appraisal_expiration_date: moment(value, "YYYY-MM-DD").add(90, "days").format("YYYY-MM-DD"),
      }));
    } else if (fieldId === "sub_type") {
      setSubType(value);
    }
  };

  const handleLocationChange = (locationData: any, fieldId: string) => {
    setFieldValues(prevValues => ({
      ...prevValues,
      address: locationData.formatted,
      city: locationData.city,
      state: locationData.state,
      zip_code: locationData.zip,
      county: locationData.county,
    }));
    setLocationData(locationData);
  };

  const handleDateChange = (date: string | null, fieldId: string) => {
    if (date) {
      handleFieldUpdate(fieldId, date);
    } else {
      handleFieldUpdate(fieldId, null);
    }
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      if (!property || !property.id) {
        throw new Error("Property is null");
      }

      const updatedFields = propertyDetailFields
        .map(field => {
          const fieldValue = fieldValues[field.data_key];

          if (fieldValue !== undefined && fieldValue !== null && fieldValue !== "") {
            const valueToSave = field.type === "boolean" ? String(fieldValue) : fieldValue ?? "";

            return {
              id: field.id,
              values: [{ value: { field: valueToSave } }],
              hasChanges: true,
              property: property.id,
              data_key: field.data_key,
              label: field.label,
              type: field.type,
              options: field.options ?? [],
              required: field.required,
              order: field.order,
            };
          }
          return null;
        })
        .filter(Boolean);

      // Include individual unit fields dynamically
      const unitInfoField = propertyDetailFields.find(
        field => field.data_key === "individual_unit_information"
      );

      if (unitInfoField && unitInfoField.fields) {
        for (let index = 1; index <= numberOfUnits; index++) {
          unitInfoField.fields.forEach(field => {
            const unitFieldId = `${field.data_key}_${index}`;
            const unitFieldValue = fieldValues[unitFieldId];

            if (unitFieldValue !== undefined && unitFieldValue !== null && unitFieldValue !== "") {
              updatedFields.push({
                id: unitFieldId,
                values: [{ value: { field: unitFieldValue } }],
                hasChanges: true,
                property: property.id,
                data_key: unitFieldId,
                label: `${field.label} (Unit ${index})`,
                type: field.type,
                options: field.options ?? [],
                required: field.required,
                order: index,
              });
            }
          });
        }
      }

      // Call the bulk insert API only if there are fields to update
      if (updatedFields.length > 0) {
        await apiRequest("/supabase/properties/property_detail/bulk-insert", updatedFields);

        showSuccess({
          title: "Saved Changes",
          description: "The property details have been updated.",
        });

        const updatedPropertyDetails = await apiRequest(
          "/supabase/properties/property_detail/select",
          { id: property.id }
        );

        setPropertyDetailFields(updatedPropertyDetails.property_fields);

        onSave();
      } else {
        showError({
          title: "No Changes",
          description: "No fields to update.",
        });
      }
    } catch (err: any) {
      showError({
        title: "Save Failed",
        description: getErrorMessage(err),
      });
    } finally {
      setSaving(false);
    }
  };

  const renderIndividualUnitInfo = () => {
    if (subType !== "Flat" || numberOfUnits <= 0) {
      return null;
    }

    const unitInfoField = propertyDetailFields.find(
      field => field.data_key === "individual_unit_information"
    );
    if (!unitInfoField || !unitInfoField.fields) return null;

    return Array.from({ length: numberOfUnits }, (_, index) => {
      const unitIndex = index + 1;
      return (
        <Box key={`unit-${unitIndex}`} mt={6}>
          <Heading size="md" mb={4}>
            Unit {unitIndex}
          </Heading>
          <SimpleGrid columns={2} spacingX="20px">
            {unitInfoField.fields?.map(field => (
              <FormControl
                key={`${field.data_key}_${unitIndex}_${field.id}`}
                isRequired={field.required}
                w="100%"
                alignItems="flex-start"
                mb="22px"
              >
                <FormLabel fontSize="14px" fontWeight="500" mb="0px">
                  {field.label} (Unit {unitIndex})
                </FormLabel>
                {field.type === "dollar" && (
                  <MoneyField
                    cy={field.data_key}
                    value={fieldValues[`${field.data_key}_${unitIndex}`] ?? ""}
                    onChange={val =>
                      handleFieldUpdate(`${field.data_key}_${unitIndex}`, val.target.value)
                    }
                    isInvalid={false}
                    disabled={!isEditMode}
                  />
                )}
                {field.type === "number" && (
                  <Input
                    type="number"
                    data-cy={field.data_key}
                    value={fieldValues[`${field.data_key}_${unitIndex}`] ?? ""}
                    onChange={ev =>
                      handleFieldUpdate(`${field.data_key}_${unitIndex}`, ev.target.value)
                    }
                    placeholder={`Enter ${field.label}`}
                    variant="primary"
                    isInvalid={false}
                    isDisabled={!isEditMode}
                  />
                )}
                {(field.type === "text" || field.type === "encrypted") && (
                  <Input
                    data-cy={field.data_key}
                    value={fieldValues[`${field.data_key}_${unitIndex}`] ?? ""}
                    onChange={ev =>
                      handleFieldUpdate(`${field.data_key}_${unitIndex}`, ev.target.value)
                    }
                    placeholder={`Enter ${field.label}`}
                    variant="primary"
                    isInvalid={false}
                    isDisabled={!isEditMode}
                  />
                )}
                {field.type === "boolean" && (
                  <RadioGroup
                    onChange={value =>
                      handleFieldUpdate(`${field.data_key}_${unitIndex}`, value === "true")
                    }
                    value={fieldValues[`${field.data_key}_${unitIndex}`]?.toString() ?? "false"}
                    isDisabled={!isEditMode}
                  >
                    <Stack direction="row">
                      <Radio value="true">Yes</Radio>
                      <Radio value="false">No</Radio>
                    </Stack>
                  </RadioGroup>
                )}
              </FormControl>
            ))}
          </SimpleGrid>
        </Box>
      );
    });
  };

  const renderField = (field: PropertyField) => {
    const fieldId = field.data_key;
    const fv = fieldValues[fieldId];

    const value = String(fv?.value ?? fv?.values?.[0]?.value ?? fv ?? "");

    return (
      <FormControl
        isRequired={field.required ?? false}
        key={`${fieldId}`}
        w="100%"
        alignItems="flex-start"
        mb="22px"
      >
        <FormLabel fontSize="14px" fontWeight="500" mb="0px">
          {field.label}
        </FormLabel>
        {(field.type === "text" || field.type === "encrypted") && (
          <Input
            data-cy={field.label}
            value={value}
            onChange={ev => handleFieldUpdate(fieldId, ev.target.value)}
            placeholder={field.placeholder ?? ""}
            variant="primary"
            isInvalid={missingFieldIds.includes(fieldId)}
            isDisabled={!isEditMode}
          />
        )}
        {field.type === "dropdown" && (
          <Select
            flex="1"
            value={value}
            isDisabled={!isEditMode}
            onChange={ev => handleFieldUpdate(fieldId, ev.target.value)}
          >
            <option value="">{field.placeholder || "Select an option"}</option>
            {field.options?.map(option => (
              <option
                key={`${fieldId}_option_${option}`}
                value={String(typeof option === "string" ? option : option)}
              >
                {typeof option === "string" ? option : option}
              </option>
            ))}
          </Select>
        )}
        {field.type === "number" && (
          <Input
            data-cy={field.label}
            type="number"
            flex="1"
            value={value}
            onChange={ev => handleFieldUpdate(fieldId, ev.target.value)}
            placeholder={field.placeholder ?? ""}
            variant="primary"
            minHeight="2.5rem"
            isInvalid={missingFieldIds.includes(fieldId)}
            isDisabled={!isEditMode}
          />
        )}
        {field.type === "dollar" && (
          <MoneyField
            cy={field.label ?? ""}
            value={value || ""}
            onValueChange={val => handleFieldUpdate(fieldId, val)}
            isInvalid={missingFieldIds.includes(fieldId)}
            disabled={!isEditMode}
          />
        )}
        {field.type === "percentage" && (
          <PercentField
            cy={field.label ?? ""}
            value={value}
            onChange={val => handleFieldUpdate(fieldId, val)}
            isInvalid={missingFieldIds.includes(fieldId)}
            disabled={!isEditMode}
          />
        )}
        {field.type === "textarea" && (
          <Textarea
            data-cy={field.label}
            value={value}
            rows={4}
            flex="1"
            resize="none"
            onChange={ev => handleFieldUpdate(fieldId, ev.target.value)}
            placeholder={field.placeholder ?? "Enter some details..."}
            isDisabled={!isEditMode}
          />
        )}
        {field.type === "location" && (
          <LocationAutocomplete
            onChange={val => handleLocationChange(val, fieldId)}
            isInvalid={missingFieldIds.includes(fieldId)}
            disabled={!isEditMode}
            defaultValue={value}
          />
        )}
        {field.type === "radio" && field.options && (
          <RadioGroup
            flex="1"
            onChange={value => handleFieldUpdate(fieldId, value)}
            isDisabled={!isEditMode}
            value={value}
          >
            <Stack>
              {field.options.map(option => {
                const optionValue = typeof option === "string" ? option : option;
                const optionLabel = typeof option === "string" ? option : option;
                return (
                  <Radio key={`${field.id}_option_${option}`} value={optionValue}>
                    {optionLabel}
                  </Radio>
                );
              })}
            </Stack>
          </RadioGroup>
        )}
        {field.type === "date" && (
          <Input
            type="date"
            value={moment(value).format("YYYY-MM-DD")}
            onChange={ev => handleDateChange(ev.target.value, fieldId)}
            position="relative"
            width="100%"
            border="1px solid #E2E8F0"
            p="6.5px"
            borderRadius="5px"
          />
        )}
        {field.type === "boolean" && (
          <RadioGroup
            onChange={value => handleFieldUpdate(fieldId, value === "true")}
            value={value?.toString()}
            isDisabled={!isEditMode}
          >
            <Stack direction="row">
              <Radio value="true">Yes</Radio>
              <Radio value="false">No</Radio>
            </Stack>
          </RadioGroup>
        )}
      </FormControl>
    );
  };

  const renderAmplendIndividualUnitInfo = () => {
    const unitInfoField = propertyDetailFields.find(
      field => field.data_key === "individual_unit_information"
    );
    if (!unitInfoField || !unitInfoField.fields) return null;

    return Array.from({ length: numberOfUnits }, (_, index) => {
      const unitIndex = index + 1;
      return (
        <Box key={`unit-${unitIndex}`} mt={6}>
          <Heading size="md" mb={4}>
            Unit {unitIndex}
          </Heading>
          <SimpleGrid columns={2} spacingX="20px">
            {unitInfoField.fields?.map(field =>
              renderField({
                ...field,
                data_key: `${field.data_key}_${unitIndex}`,
                label: `${field.label} (Unit ${unitIndex})`,
              })
            )}
          </SimpleGrid>
        </Box>
      );
    });
  };

  const renderSectionActions = (section: string, sectionIndex: number) => {
    return (
      <Flex alignItems="center" justifyContent="space-between" mb={4}>
        <BoxHeader title={section} />
        {sectionIndex === 0 && (
          <Flex alignItems="center" gap={2}>
            {isEditMode && (
              <Button onClick={onCancel} variant="outline" size="sm">
                Cancel
              </Button>
            )}
            <Button
              onClick={isEditMode ? saveChanges : onSave}
              isLoading={saving}
              loadingText="Saving"
              size="sm"
              leftIcon={
                isEditMode ? <FontAwesomeIcon icon={faSave} /> : <FontAwesomeIcon icon={faEdit} />
              }
            >
              {isEditMode ? "Save Changes" : "Edit"}
            </Button>
          </Flex>
        )}
      </Flex>
    );
  };

  const renderSections = () => {
    const sections = propertyDetailFields.reduce((acc, field) => {
      if (!field.section) return acc;
      const section = field.section || "General";
      if (!acc[section]) acc[section] = [];
      acc[section].push(field);
      return acc;
    }, {} as Record<string, PropertyField[]>);

    return Object.entries(sections).map(([section, fields], sectionIndex) => (
      <Box key={section}>
        {renderSectionActions(section, sectionIndex)}
        <SimpleGrid columns={2} spacingX="20px">
          {fields.map(field => renderField(field))}
        </SimpleGrid>
      </Box>
    ));
  };

  const hasSections = propertyDetailFields.some(field => field.section);

  if (!propertyDetailFields) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  return (
    <Box>
      {!hasSections && renderSectionActions("Property Details", 0)}

      {hasSections ? (
        renderSections()
      ) : (
        <SimpleGrid columns={2} spacingX="20px">
          {propertyDetailFields
            .filter(field => !field.data_key.startsWith("unit_"))
            .filter(field => !field.data_key.startsWith("individual_unit_information"))
            .map(field => renderField(field))}
        </SimpleGrid>
      )}

      {isAmplend ? renderAmplendIndividualUnitInfo() : renderIndividualUnitInfo()}
    </Box>
  );
}
